import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Combobox,
  Option,
  Skeleton,
  SkeletonItem,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import { useContainers } from '@/hooks/useContainers';
import { useFolders } from '@/hooks/useFolders';
import { buildFolderTree, type FolderNode } from './buildTree';
import { useCopyFolder, useMoveFolder } from '@/hooks/useFolderMutations';
import { useRecentProjectsStore } from '@/stores/recentProjectsStore';
import { filterListableContainers } from '@/utils/listableContainer';
import type { Container } from '@/types/domain';

const useStyles = makeStyles({
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  label: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200 },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
});

/**
 * Ce que ce dialogue exige VRAIMENT du dossier à déplacer/copier : son id (pour
 * s'exclure lui-même et ses descendants des destinations), son nom (le titre) et
 * son conteneur (la destination par défaut). Ni `parentId`, ni `position`, ni
 * `path` — c'est l'arbre de la DESTINATION qui est rechargé, pas la source.
 *
 * Comme pour [MailTargetItem], la raison est qu'un résultat de recherche n'est
 * pas un `Folder` complet : `fn_search` ne renvoie pas le parent d'un dossier.
 * Un `Folder` satisfait cette forme, donc les appelants existants sont inchangés.
 */
export interface FolderTargetItem {
  id: string;
  name: string;
  containerId: string;
}

interface Props {
  open: boolean;
  folder: FolderTargetItem;
  /**
   * `move` = reparenter le dossier ; `copy` = en créer un double (arborescence +
   * mails) à la destination. Un seul dialogue pour les deux, comme
   * `MailTargetDialog` pour les mails : le choix de destination est identique,
   * seul le verbe et l'appel changent — deux dialogues jumeaux auraient divergé.
   */
  mode?: 'move' | 'copy';
  onClose: () => void;
}

function flatten(nodes: FolderNode[], depth = 0): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  for (const n of nodes) {
    out.push({ id: n.folder.id, label: `${'\u00A0\u00A0\u00A0'.repeat(depth)}${n.folder.name}` });
    out.push(...flatten(n.children, depth + 1));
  }
  return out;
}

export function MoveFolderDialog({ open, folder, mode = 'move', onClose }: Props) {
  const isCopy = mode === 'copy';
  const styles = useStyles();
  const { data: containers } = useContainers();
  const recent = useRecentProjectsStore((s) => s.recent);

  const [targetContainerId, setTargetContainerId] = useState<string>(folder.containerId);
  const [targetContainerQuery, setTargetContainerQuery] = useState<string>('');
  const [targetParentId, setTargetParentId] = useState<string | null>(null);

  const { data: targetFolders, isPending: foldersLoading } = useFolders(targetContainerId);
  const folderOptions = useMemo(() => {
    if (!targetFolders) return [];
    // Exclude the folder being moved (and its descendants) to prevent cycles.
    const blocked = new Set<string>([folder.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const f of targetFolders) {
        if (f.parentId && blocked.has(f.parentId) && !blocked.has(f.id)) {
          blocked.add(f.id);
          changed = true;
        }
      }
    }
    const usable = targetFolders.filter((f) => !blocked.has(f.id));
    return flatten(buildFolderTree(usable));
  }, [targetFolders, folder.id]);

  // 0029 : idem MailTargetDialog — pas de groupe M365, pas de destination possible.
  const all = filterListableContainers((containers ?? []) as Container[]) as Container[];
  const byId = new Map(all.map((c) => [c.id, c]));
  const recentSet = new Set(recent.map((r) => r.id));
  const sortedContainers = useMemo(() => {
    const recentOnes = recent
      .map((r) => byId.get(r.id))
      .filter((c): c is Container => Boolean(c));
    const others = all
      .filter((c) => !recentSet.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    return [...recentOnes, ...others];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, recent]);
  const filteredContainers = useMemo(() => {
    const q = targetContainerQuery.trim().toLowerCase();
    if (!q) return sortedContainers;
    return sortedContainers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.number ? c.number.toLowerCase().includes(q) : false),
    );
  }, [sortedContainers, targetContainerQuery]);

  const targetContainer = byId.get(targetContainerId) ?? null;
  const targetParentLabel =
    folderOptions.find((f) => f.id === targetParentId)?.label ?? '';

  const move = useMoveFolder();
  const copy = useCopyFolder();
  const active = isCopy ? copy : move;

  const submit = () => {
    const crossContainer =
      targetContainerId !== folder.containerId ? targetContainerId : undefined;
    if (isCopy) {
      copy.mutate(
        {
          folderId: folder.id,
          targetParentId: targetParentId,
          targetContainerId: crossContainer,
        },
        { onSuccess: () => onClose() },
      );
      return;
    }
    move.mutate(
      {
        folderId: folder.id,
        newParentId: targetParentId,
        newContainerId: crossContainer,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(_e, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {isCopy ? 'Copier' : 'Déplacer'} « {folder.name} »
          </DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className={styles.group}>
                <Text className={styles.label}>Projet ou espace de destination</Text>
                <Combobox
                  freeform
                  placeholder="Taper pour filtrer…"
                  value={targetContainerQuery || targetContainer?.name || ''}
                  selectedOptions={[targetContainerId]}
                  onInput={(e) =>
                    setTargetContainerQuery((e.target as HTMLInputElement).value)
                  }
                  onOptionSelect={(_e, data) => {
                    if (data.optionValue) {
                      setTargetContainerId(data.optionValue);
                      setTargetParentId(null);
                      const c = byId.get(data.optionValue);
                      setTargetContainerQuery(c?.name ?? '');
                    }
                  }}
                >
                  {filteredContainers.slice(0, 50).map((c) => (
                    <Option key={c.id} value={c.id} text={c.name}>
                      {c.name}
                      {c.kind === 'project' && c.number ? ` · ${c.number}` : ''}
                    </Option>
                  ))}
                </Combobox>
              </div>

              <div className={styles.group}>
                <Text className={styles.label}>Dossier parent (vide = racine)</Text>
                {foldersLoading ? (
                  <Skeleton>
                    <SkeletonItem size={32} />
                  </Skeleton>
                ) : (
                  <Combobox
                    placeholder="Racine du conteneur"
                    value={targetParentLabel || '(racine)'}
                    selectedOptions={targetParentId ? [targetParentId] : []}
                    onOptionSelect={(_e, data) =>
                      setTargetParentId(data.optionValue === '' ? null : data.optionValue ?? null)
                    }
                  >
                    <Option key="__root" value="" text="(racine)">
                      (racine)
                    </Option>
                    {folderOptions.map((f) => (
                      <Option key={f.id} value={f.id} text={f.label}>
                        {f.label}
                      </Option>
                    ))}
                  </Combobox>
                )}
              </div>

              {isCopy && (
                <Text className={styles.hint}>
                  La copie duplique l'arborescence <strong>et les mails</strong> qu'elle contient.
                  Un dossier de même nom à la destination donne « {folder.name} (copie) ». Limité à
                  100 sous-dossiers et 300 mails par copie.
                </Text>
              )}

              {active.error && (
                <Text className={styles.hint} style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {active.error.message}
                </Text>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Annuler
            </Button>
            <Button appearance="primary" disabled={active.isPending} onClick={submit}>
              {active.isPending
                ? isCopy
                  ? 'Copie…'
                  : 'Déplacement…'
                : isCopy
                  ? 'Copier'
                  : 'Déplacer'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
